/**
 * What a file is (docs/pdlc/feasibility-files.md §6). PURE; runs in the browser
 * on the Files page and wherever a file is dropped.
 *
 * One file can hold several things: a workbook with Test and Control sheets is
 * one placements list; a sheet of panelist IDs beside it is a second. Each is a
 * `FileRead` with its kind, the sheets it reads and the kinds the person may
 * switch it to. The rules are the ones the brief reader already used for an
 * attachment's lists (`location-lists.ts`, `panelist-ids.ts`, `residents.ts`),
 * so a file reads the same wherever it is dropped.
 *
 * Screens and campaign placements are never guessed from coordinates alone: a
 * campaign needs a TabID column, and screens need a screen id column on more
 * different locations than one check takes (`readsAsScreens`): a network's whole
 * list, which can't be a check's placements (2026-10-28, a 19,100-screen list read
 * as placements and was refused). Otherwise screens are the person's choice.
 */
import { panelistIdsInSheet } from '../brief/panelist-ids';
import type { ListKind } from '../contracts/pql';
import { isAddressTable } from '../ooh/addresses';
import {
    columnIndex,
    coordinateColumns,
    headerRowOf,
    isLocationTable,
    RADIUS_HEADERS,
    roleOf,
    TYPE_HEADERS,
} from '../ooh/placements';
import type { Sheet } from '../ooh/location-lists';
import { zipColumnCodes } from '../ooh/residents';
import { parseStoreLocationRows } from '../stores/store-locations';
import { MAX_OOH_PLACEMENTS } from '../engine/request';
import { looksLikeAdLog } from './ad-log';
import { hasScreenIdColumn } from './screens';
import { hasTabIdColumn } from './campaign';

/** Every kind a file can be read as: a saved list's kinds, a brief, or a campaign's ad log. */
export type FileKind = ListKind | 'brief' | 'adlog';

export interface FileRead {
    kind: FileKind;
    /** The sheets this read uses, by name (a CSV is one sheet named after the file). */
    sheets: string[];
    /** `sure` when only one reading fits; `likely` when the person should look. */
    confidence: 'sure' | 'likely';
    /** The kinds the person may switch this read to. */
    choices: FileKind[];
}

/** Files read as text by the brief reader, never as tables. */
export const BRIEF_ONLY = /\.(eml|pdf|docx?|png|jpe?g|webp|txt)$/i;
/** Files read as sheets. */
export const SPREADSHEET = /\.(csv|xlsx|xls)$/i;

const LOCATION_CHOICES: FileKind[] = ['placements', 'stores', 'screens', 'campaign'];

/** A sheet of store or chain names, one per row, with no coordinates. */
function isNameList(rows: string[][]): boolean {
    const filled = rows.filter((r) => r.some((c) => String(c ?? '').trim()));
    if (filled.length < 2) return false;
    const wide = filled.filter((r) => r.filter((c) => String(c ?? '').trim()).length > 2).length;
    const wordy = filled.filter((r) => /[a-z]/i.test(String(r[0] ?? ''))).length;
    return wide <= filled.length * 0.1 && wordy >= filled.length * 0.8;
}

/** A located list with groups: a test / control column, or sheets named Test and Control. */
function hasGroups(sheets: Sheet[]): boolean {
    if (sheets.length > 1 && sheets.some((s) => roleOf(s.name))) return true;
    return sheets.some((s) => {
        const h = headerRowOf(s.rows);
        return (
            h >= 0 &&
            s.rows[h].some((c) =>
                /^(group|cell|test\s*\/?\s*control|test or control|exposure)$/i.test(
                    String(c ?? '').trim(),
                ),
            )
        );
    });
}

/**
 * A network's screens rather than a check's placements: every sheet has a screen id
 * column, and together they hold more different locations than one check takes.
 */
function readsAsScreens(sheets: Sheet[]): boolean {
    const places = new Set<string>();
    for (const s of sheets) {
        if (!hasScreenIdColumn(s.rows)) return false;
        const h = headerRowOf(s.rows);
        const { lat, lng } = coordinateColumns(s.rows, h);
        for (const r of s.rows.slice(h + 1)) {
            const y = Number(String(r[lat] ?? '').trim());
            const x = Number(String(r[lng] ?? '').trim());
            if (Number.isFinite(y) && Number.isFinite(x) && (y || x))
                places.add(`${y.toFixed(6)},${x.toFixed(6)}`);
        }
    }
    return places.size > MAX_OOH_PLACEMENTS;
}

/** Stores rather than placements: a store or chain name on most rows, and no media type or radius. */
function readsAsStores(sheets: Sheet[]): boolean {
    return sheets.every((s) => {
        const h = headerRowOf(s.rows);
        if (h < 0) return false;
        const headers = s.rows[h].map((c) => String(c ?? ''));
        if (columnIndex(headers, TYPE_HEADERS) >= 0 || columnIndex(headers, RADIUS_HEADERS) >= 0)
            return false;
        const read = parseStoreLocationRows(s.rows);
        return read.locations.length > 0 && read.locations.length >= read.errors.length;
    });
}

export function classifyFile(fileName: string, sheets: Sheet[] | null): FileRead[] {
    const brief: FileRead = { kind: 'brief', sheets: [], confidence: 'sure', choices: ['brief'] };
    if (!sheets || BRIEF_ONLY.test(fileName)) return [brief];
    const filled = sheets.filter((s) => s.rows.some((r) => r.some((c) => String(c ?? '').trim())));
    if (!filled.length) return [];
    if (filled.length === 1 && looksLikeAdLog(fileName, filled[0].rows))
        return [
            { kind: 'adlog', sheets: [filled[0].name], confidence: 'sure', choices: ['adlog'] },
        ];

    const reads: FileRead[] = [];
    const located: Sheet[] = [];
    const campaign: Sheet[] = [];
    const addressed: Sheet[] = [];
    const prose: Sheet[] = [];
    for (const s of filled) {
        if (isLocationTable(s.rows)) {
            (hasTabIdColumn(s.rows) ? campaign : located).push(s);
            continue;
        }
        if (panelistIdsInSheet(s)) {
            reads.push({
                kind: 'panelists',
                sheets: [s.name],
                confidence: 'sure',
                choices: ['panelists'],
            });
            continue;
        }
        if (zipColumnCodes(s.rows)?.length) {
            reads.push({ kind: 'zips', sheets: [s.name], confidence: 'sure', choices: ['zips'] });
            continue;
        }
        if (isAddressTable(s.rows)) {
            addressed.push(s);
            continue;
        }
        if (isNameList(s.rows)) {
            reads.push({
                kind: 'stores',
                sheets: [s.name],
                confidence: 'likely',
                choices: ['stores'],
            });
            continue;
        }
        prose.push(s);
    }
    if (campaign.length)
        reads.unshift({
            kind: 'campaign',
            sheets: campaign.map((s) => s.name),
            confidence: 'sure',
            choices: LOCATION_CHOICES,
        });
    if (located.length) {
        const kind: FileKind = hasGroups(located)
            ? 'placements'
            : readsAsScreens(located)
              ? 'screens'
              : readsAsStores(located)
                ? 'stores'
                : 'placements';
        reads.unshift({
            kind,
            sheets: located.map((s) => s.name),
            confidence: kind === 'placements' && hasGroups(located) ? 'sure' : 'likely',
            choices: LOCATION_CHOICES,
        });
    }
    if (addressed.length)
        reads.push({
            kind: 'placements',
            sheets: addressed.map((s) => s.name),
            confidence: 'likely',
            choices: ['placements', 'stores'],
        });
    // A workbook with no list in it is a brief (a questionnaire, a media plan written out).
    if (!reads.length && prose.length) return [{ ...brief, confidence: 'likely' }];
    return reads;
}
